import { useContext, useEffect, useRef, useState } from "react";
import { BaseInputPropsType } from "./Input";
import { getFormContext } from "config/FormContext";
import { InputErrorType } from "types/form-types";
import { validateInput } from "utilities/input-utilities";
import InputLabel from "./input-common/InputLabel";
import HTMLCode from "components/common/HTMLCode";
import InputErrors from "./input-common/InputErrors";

export type RadioButtonInputPropsType<T> = BaseInputPropsType<HTMLInputElement, T> & {
    data: T[],
    itemKey?: (i: T) => string | number;
    display?: (i: T) => string,
}

const RadioButtonInput = <T,>(props: RadioButtonInputPropsType<T>) => {
    const input = useRef<HTMLInputElement>(null);
    const inputWrapper = useRef<HTMLDivElement>(null);
    let [touched, setTouched] = useState(props.touched ? true : false);
    let [errors, setErrors] = useState<InputErrorType[]>([]);
    const formContext = getFormContext(useContext);

    useEffect(() => {
        validate(!touched);
    }, []);

    useEffect(() => {
        validate(!touched);
    }, [props.value]);

    useEffect(() => {
        setTouched(formContext.submitted);
        validate(!formContext.submitted);
    }, [formContext.submitted])

    const onChange = (e: React.ChangeEvent<HTMLInputElement>, item: T, index: number) => {

        if (typeof props.onChange == 'function') {
            props.onChange(item, e);
        }

        setTouched(true);
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setTouched(true);
        validate();
        if (typeof props.onBlur == 'function') {
            props.onBlur(props.value, e);
        }
    }

    function validate(soft?: boolean) {
        let currentCustomErrors = props.customErrors ? [...props.customErrors] : [];

        // if(props.required) {
        //     currentCustomErrors.push({
        //         message: 'form.errors.valueMissing',
        //         name: 'valueMissing',
        //         validate(value, input) {
        //             return value?.length ? true : false;
        //         },
        //     })
        // }

        setErrors(validateInput<T, HTMLInputElement>({
            value: props.value,
            input: input?.current || undefined,
            inputWrapper: inputWrapper?.current || undefined,
            customErrors: currentCustomErrors,
            soft: soft ? true : false
        }) || []);
    }

    const display = (item: T) => {
        if(typeof props.display == 'function') return props.display(item);
        if(typeof item === 'object') return (item as any).name;
        return item;
    }

    const getKey = (item: T) => {
        if(typeof props.itemKey == 'function') return props.itemKey(item);
        if(typeof item === 'object') return (item as any).id;
        return item;
    }

    return (
        <div className="app-input-wrapper checkbox" ref={inputWrapper}>
            <input ref={input} type='checkbox' className="hidden" placeholder="shit" />
            <InputLabel label={props.label} required={props.required} className='my-auto' />
            <ul>
                {props.data.map((c, i) => {
                    const key = getKey(c);
                    return (
                        <li key={key}>
                            <label className="inline-flex">
                                <input
                                    type="radio"
                                    name={props.name}
                                    autoFocus={props.autoFocus}
                                    value={key}
                                    onChange={v => onChange(v, c, i)}
                                    placeholder="checkbox"
                                    onBlur={onBlur}
                                    checked={props.value !== undefined && props.value !== null && key === getKey(props.value)}
                                    disabled={props.disabled}
                                    readOnly={props.readonly}
                                    className="accent-primary"
                                />
                                <HTMLCode />
                                <InputLabel label={display(c)} removeDoublePoints className='my-auto' />
                            </label>
                        </li>
                    );
                })}
            </ul>
            <InputErrors errors={errors} />
        </div>
    );
};

export default RadioButtonInput;
